import React, { useEffect, useState, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import _ from 'lodash';
import type { OrderItemStepTodoFieldStore } from '@this/src/domain/arrangement/order_item_step_todo_field_store';
import { makeStyles } from '@material-ui/core/styles';
import { Fetcher, HTTPError, isAiTravel } from '@this/src/util';
import type { Message } from '@this/components/trips_management/trips/trips_chat/types';
import UserMessage from '@this/components/arrangement/virtual_counter/virtual_counter_body/message/user_message';
import PartnerMessage from '@this/components/arrangement/virtual_counter/virtual_counter_body/message/partner_message';
import Logs from '@this/components/arrangement/virtual_counter/virtual_counter_body/message/logs';
import OrderItemEditForm from '@this/components/arrangement/shared/price_change_form/price_change_form_item/order_item_edit_form';
import OrderItemPriceForm from '@this/components/arrangement/shared/price_change_form/price_change_form_item/order_item_price_form/order_item_price_form';
import { useAITTheme } from '@this/shared/ui/theme';
import { Link } from '@this/shared/ui/navigations/link';
import Notification from '@this/src/notification';
import { HotelPriceRegulationInfo } from '@this/components/arrangement/shared/hotel_price_regulation_info/hotel_price_regulation_info';
import { SmasInfo } from '@this/components/arrangement/shared/smas_info/smas_info';
import TimeZoneLib from '../../../../../../lib/time_zone.json';
import { OrderInfo } from '../../virtual_counter/virtual_counter_aside/order_info';
import OrderItemStepSection from './order_item_step_section';
import { OrganizationInfoButton } from '../../virtual_counter/virtual_counter_aside/organization_info_button';
import { BookerInfo } from '../../virtual_counter/virtual_counter_aside/booker_info';
import { TravelRegulationUserInfo } from '../../virtual_counter/virtual_counter_aside/travel_regulation_user_info';
import { UserMemoField } from '../../virtual_counter/virtual_counter_aside/user_memo_field';
import { TripInfo } from '../../virtual_counter/virtual_counter_aside/trip_info';
import { OrderItemTransportInfo } from '../../virtual_counter/virtual_counter_aside/order_item_transport_info';
import { OrderItemAioperatorInfo } from '../../virtual_counter/virtual_counter_aside/order_item_aioperator_info';
import { OrderItemReservationUrls } from '../../virtual_counter/virtual_counter_aside/order_item_reservation_urls';
import { JsonViewer } from '../../virtual_counter/virtual_counter_aside/json_viewer';
import { HotelInfo } from '../../virtual_counter/virtual_counter_aside/hotel_info';
import { VirtualCounterMessageForm } from '../../virtual_counter/virtual_counter_body/virtual_counter_message_form';
import type { VirtualCounterMessageFormData } from '../../virtual_counter/virtual_counter_body/virtual_counter_message_form';
import { StatusChangeLog } from '../../virtual_counter/virtual_counter_aside/status_change_log';
import { VirtualCounterProvider } from '../../virtual_counter/virtual_counter_context';
import { OrderItemWaitingSelect } from './order_item_waiting_select';
import { DomesticAirPurchaseDeadlineForm } from '../../shared/price_change_form/price_change_form_item/domestic_air_purchase_deadline_form';
import { OrderItemMessageLogs } from './order_item_message_logs';

interface Props {
  serviceId: number;
  store: OrderItemStepTodoFieldStore;
}

export interface OrderItemStepWorkDialogContentHandle {
  refetchAndScrollToBottom: () => void;
  scrollToBottom: () => void;
}

const STORAGE_KEY_LEFT_WIDTH = 'order_item_step_work_dialog_left_width';
const STORAGE_KEY_CENTER_WIDTH = 'order_item_step_work_dialog_center_width';

const OrderItemStepWorkDialogContent = forwardRef<OrderItemStepWorkDialogContentHandle, Props>(
  ({ serviceId, store }, ref) => {
    const { trip, order, orderItem } = store;
    const classes = useStyles();
    const { theme } = useAITTheme();
    const [messages, setMessages] = useState<Message[]>([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [showMessageStates, setShowMessageStates] = useState<Record<number, boolean>>({});
    const [edittingMessage, setEdittingMessage] = useState<Message | null>(null);

    const leftColumnRef = useRef<HTMLDivElement>(null);
    const centerColumnRef = useRef<HTMLDivElement>(null);
    const rightColumnRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const messageContainerRef = useRef<HTMLDivElement>(null);
    const [isResizingLeft, setIsResizingLeft] = useState(false);
    const [isResizingRight, setIsResizingRight] = useState(false);
    const [isHoveredLeft, setIsHoveredLeft] = useState(false);
    const [isHoveredRight, setIsHoveredRight] = useState(false);

    const getStoredWidth = useCallback((key: string, defaultValue: number): number => {
      const stored = localStorage.getItem(key);
      if (stored) {
        const width = parseInt(stored, 10);
        if (!isNaN(width) && width > 0) {
          return width;
        }
      }
      return defaultValue;
    }, []);

    const saveWidth = useCallback((key: string, width: number) => {
      localStorage.setItem(key, width.toString());
    }, []);

    useEffect(() => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const leftWidth = getStoredWidth(STORAGE_KEY_LEFT_WIDTH, containerWidth * 0.5);
      const centerWidth = getStoredWidth(STORAGE_KEY_CENTER_WIDTH, containerWidth * 0.25);

      if (leftColumnRef.current) {
        leftColumnRef.current.style.width = `${leftWidth}px`;
      }
      if (centerColumnRef.current) {
        centerColumnRef.current.style.width = `${centerWidth}px`;
      }
    }, [getStoredWidth]);

    const handleResizeLeft = useCallback(
      (e: MouseEvent) => {
        if (!leftColumnRef.current || !containerRef.current || !isResizingLeft) return;

        const containerWidth = containerRef.current.offsetWidth;
        const handleWidth = 2;
        const leftWidth = e.clientX - containerRef.current.getBoundingClientRect().left - handleWidth;
        const minWidth = containerWidth * 0.2;
        const maxWidth = containerWidth * 0.7;

        const validLeftWidth = Math.max(minWidth, Math.min(maxWidth, leftWidth));

        if (leftColumnRef.current) {
          leftColumnRef.current.style.width = `${validLeftWidth}px`;
        }
        saveWidth(STORAGE_KEY_LEFT_WIDTH, validLeftWidth);
      },
      [isResizingLeft, saveWidth]
    );

    const handleResizeRight = useCallback(
      (e: MouseEvent) => {
        if (!centerColumnRef.current || !containerRef.current || !isResizingRight) return;

        const containerWidth = containerRef.current.offsetWidth;
        const handleWidth = 2;
        const leftWidth = leftColumnRef.current?.offsetWidth || containerWidth * 0.5;
        const centerWidth =
          e.clientX - containerRef.current.getBoundingClientRect().left - leftWidth - handleWidth * 2;
        const minWidth = containerWidth * 0.15;
        const maxWidth = containerWidth * 0.4;

        const validCenterWidth = Math.max(minWidth, Math.min(maxWidth, centerWidth));

        if (centerColumnRef.current) {
          centerColumnRef.current.style.width = `${validCenterWidth}px`;
        }
        saveWidth(STORAGE_KEY_CENTER_WIDTH, validCenterWidth);
      },
      [isResizingRight, saveWidth]
    );

    useEffect(() => {
      if (isResizingLeft) {
        const handleMouseUp = () => setIsResizingLeft(false);
        document.addEventListener('mousemove', handleResizeLeft);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
          document.removeEventListener('mousemove', handleResizeLeft);
          document.removeEventListener('mouseup', handleMouseUp);
        };
      }
      return undefined;
    }, [isResizingLeft, handleResizeLeft]);

    useEffect(() => {
      if (isResizingRight) {
        const handleMouseUp = () => setIsResizingRight(false);
        document.addEventListener('mousemove', handleResizeRight);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
          document.removeEventListener('mousemove', handleResizeRight);
          document.removeEventListener('mouseup', handleMouseUp);
        };
      }
      return undefined;
    }, [isResizingRight, handleResizeRight]);

    const scrollToBottom = useCallback(() => {
      if (messageContainerRef.current) {
        messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
      }
    }, []);

    const fetchMessages = useCallback(
      (shouldScrollToBottom = false) => {
        if (!trip?.id) return;

        setLoadingMessages(true);
        Fetcher.get<{ messages: Message[] }>(`/trips/${trip.id}/messages.json`, { type: 'arranger' })
          .then(result => {
            setMessages(result.messages || []);
            setLoadingMessages(false);
            if (shouldScrollToBottom) {
              setTimeout(scrollToBottom, 100);
            }
          })
          .catch(() => {
            setLoadingMessages(false);
          });
      },
      [trip?.id, scrollToBottom]
    );

    useImperativeHandle(ref, () => ({
      refetchAndScrollToBottom: () => {
        fetchMessages(true);
      },
      scrollToBottom
    }));

    useEffect(() => {
      fetchMessages(true);
    }, [fetchMessages]);

    const handleMessageSubmit = useCallback(
      async ({ message, replyRequired, files, registeredFiles, deleteFiles }: VirtualCounterMessageFormData) => {
        if (!trip) {
          return false;
        }

        const formData = new FormData();
        formData.append('message', message);
        formData.append('reply_required', replyRequired.toString());
        files.forEach(file => formData.append('files[]', file));
        registeredFiles.forEach(file => formData.append('registeredFiles[]', file));
        formData.append('type', 'partner');

        let endpointUrl = `/trips/${trip.id}/messages`;
        let method: 'POST' | 'PATCH' = 'POST';
        if (edittingMessage) {
          deleteFiles.forEach(f => formData.append('delete_file_paths[]', f.file_path));

          endpointUrl = `/trips/${edittingMessage.trip_id}/messages/${edittingMessage.id}`;
          method = 'PATCH';
        }

        try {
          await Fetcher.upload(endpointUrl, formData, { method });
          setEdittingMessage(null);
          fetchMessages();
          return true;
        } catch (e) {
          let errorMessage = '送信に失敗しました。';
          if (e instanceof HTTPError && e.response?.status === 413) {
            errorMessage = 'ファイルサイズが大きすぎます。1MB以内のファイルを指定してください。';
            if (e.response.data.errors) {
              errorMessage = e.response.data.errors.join('\n');
            }
          }
          Notification.error(errorMessage);
          return false;
        }
      },
      [edittingMessage, trip, fetchMessages]
    );

    const classNameForModifiedField = useCallback(
      (...path: (string | number)[]): string => {
        if (!store.orderItem) return '';
        const initialOrderItem = store.trip.initialOrderItemFor(store.orderItem);
        const orderItemDiff = store.orderItem.diffWith(initialOrderItem);
        const modified = orderItemDiff ? _.has(orderItemDiff, path) : true;
        return modified ? 'virtual-counte-price-change-form__modified_field' : '';
      },
      [store]
    );

    return (
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          width: '100%',
          height: '83vh',
          overflow: 'hidden'
        }}
      >
        <div
          ref={leftColumnRef}
          className={classes.scrollableGrid}
          style={{
            display: 'flex',
            flexDirection: 'column',
            width: '50%',
            minWidth: '200px',
            maxWidth: '70%'
          }}
        >
          <div
            ref={messageContainerRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              border: `1px solid ${theme.fieldBorderColor}`,
              borderRadius: '4px',
              padding: '8px',
              marginBottom: '16px',
              minHeight: 0
            }}
          >
            {loadingMessages ? (
              <div style={{ textAlign: 'center', color: theme.grayTextColor, padding: '16px' }}>読み込み中...</div>
            ) : messages.length === 0 ? (
              <div style={{ textAlign: 'center', color: theme.grayTextColor, padding: '16px' }}>
                メッセージがありません
              </div>
            ) : trip ? (
              messages.map(message => {
                if (message.type === 'user') {
                  return (
                    <UserMessage
                      key={message.id}
                      message={message}
                      selectedTrip={trip}
                      fetchMessages={fetchMessages}
                      handleMessageEdit={() => setEdittingMessage(message)}
                    />
                  );
                }
                if (message.type === 'partner') {
                  return (
                    <PartnerMessage
                      key={message.id}
                      message={message}
                      selectedTrip={trip}
                      fetchMessages={fetchMessages}
                      handleMessageEdit={() => setEdittingMessage(message)}
                    />
                  );
                }
                if (
                  ['partner_log', 'status_log', 'order_item_status_log', 'order_item_billed_at_log'].includes(
                    message.type
                  )
                ) {
                  return (
                    <Logs
                      key={message.id}
                      message={message}
                      selectedTrip={trip}
                      showMessage={showMessageStates[message.id] || false}
                      toggleShowMessage={() => {
                        setShowMessageStates(prev => ({
                          ...prev,
                          [message.id]: !prev[message.id]
                        }));
                      }}
                      fetchMessages={fetchMessages}
                    />
                  );
                }
                return null;
              })
            ) : null}
          </div>
          {trip && (
            <div
              style={{
                borderTop: `1px solid ${theme.fieldBorderColor}`,
                padding: '16px'
              }}
            >
              <VirtualCounterMessageForm
                edittingMessage={edittingMessage}
                messageTemplates={store.messageTemplates}
                selectedTrip={trip}
                onSubmit={handleMessageSubmit}
                onClickEditCancel={() => setEdittingMessage(null)}
                serviceId={serviceId}
              />
            </div>
          )}
        </div>
        {trip && order && orderItem ? (
          <VirtualCounterProvider suppliedItems={store.suppliedItems}>
            <>
              <div
                onMouseDown={e => {
                  e.preventDefault();
                  setIsResizingLeft(true);
                }}
                onMouseEnter={() => setIsHoveredLeft(true)}
                onMouseLeave={() => setIsHoveredLeft(false)}
                style={{
                  width: isHoveredLeft || isResizingLeft ? '4px' : '2px',
                  backgroundColor: '#E0E0E0',
                  cursor: 'col-resize',
                  transition: isResizingLeft ? 'none' : 'width 0.2s',
                  flexShrink: 0
                }}
              />
              <div
                ref={centerColumnRef}
                className={classes.scrollableGrid}
                style={{
                  width: '25%',
                  minWidth: '150px',
                  maxWidth: '40%'
                }}
              >
                <OrderItemStepSection>
                  <UserMemoField trip={trip} />
                </OrderItemStepSection>
                <OrderItemStepSection>
                  <OrganizationInfoButton trip={trip} />
                  <SmasInfo trip={trip} />
                </OrderItemStepSection>
                <OrderItemStepSection>
                  <OrderItemTransportInfo
                    orderItem={orderItem}
                    showTicketOprTicketing={isAiTravel(serviceId)}
                    ticketOprResponse={store.ticketOprResponsePrice || []}
                    onReloadTrip={() => {}}
                  />
                </OrderItemStepSection>
                <OrderItemStepSection>
                  <OrderItemAioperatorInfo trip={trip} orderItem={orderItem} />
                </OrderItemStepSection>
                <OrderItemStepSection>
                  <OrderItemReservationUrls orderItem={orderItem} />
                </OrderItemStepSection>
                <div className="virtual-counter-partner__pay-request-form" data-testid="price-change-form-item">
                  <div style={{ marginBottom: '8px' }}>
                    <OrderItemWaitingSelect orderItem={orderItem} />
                  </div>
                  <DomesticAirPurchaseDeadlineForm
                    orderItem={orderItem}
                    classNameForModifiedField={classNameForModifiedField}
                  />
                  <OrderItemEditForm
                    order={order}
                    orderItem={orderItem}
                    selectedTrip={trip}
                    serviceId={serviceId}
                    cabinOptions={[]}
                    individualTargetSuppliedItems={store.individualTargetSuppliedItems}
                    individualDomesticAirSuppliedItems={store.individualDomesticAirSuppliedItems}
                    handleRemoveElement={() => {}}
                    updateIsAirNumbersMissing={() => {}}
                    hotelElementProviderOptions={order.hotelElementProviderOptions || {}}
                    carProviderOptions={order.carProviderOptions}
                    carTypeOptions={order.carTypeOptions}
                    shippingCategoryOptions={order.shippingCategoryOptions}
                    wifiProviderOptions={order.wifiProviderOptions}
                    updateForeignAirTransportElements={() => {}}
                    updatePriceDetailsFromPnr={() => {}}
                    timeZones={TimeZoneLib as Array<{ label: string; value: string }>}
                  />
                </div>
                <OrderItemStepSection>
                  <OrderInfo trip={trip} />
                </OrderItemStepSection>
                <OrderItemStepSection>
                  <h3 style={{ marginBottom: '4px' }}>料金プラン</h3>
                  <p>{store.organizationPlan}</p>
                </OrderItemStepSection>
                <OrderItemStepSection>
                  <BookerInfo trip={trip} />
                </OrderItemStepSection>
                <OrderItemStepSection>
                  <TravelRegulationUserInfo trip={trip} />
                </OrderItemStepSection>
                <OrderItemStepSection>
                  <HotelPriceRegulationInfo trip={trip} />
                </OrderItemStepSection>
                <OrderItemStepSection>
                  <TripInfo trip={trip} />
                </OrderItemStepSection>
                {trip.search_query && (
                  <OrderItemStepSection>
                    <JsonViewer label="検索クエリ" json={trip.search_query} />
                  </OrderItemStepSection>
                )}
                <OrderItemStepSection>
                  <HotelInfo trip={trip} />
                </OrderItemStepSection>
                <OrderItemStepSection>
                  <Link
                    href={`/arrangement/order_histories?type=trip_id&trip_id=${trip.id}`}
                    target="_blank"
                    isExternal
                  >
                    この旅程の履歴を見る
                  </Link>
                </OrderItemStepSection>
              </div>
              <div
                onMouseDown={e => {
                  e.preventDefault();
                  setIsResizingRight(true);
                }}
                onMouseEnter={() => setIsHoveredRight(true)}
                onMouseLeave={() => setIsHoveredRight(false)}
                style={{
                  width: isHoveredRight || isResizingRight ? '4px' : '2px',
                  backgroundColor: '#E0E0E0',
                  cursor: 'col-resize',
                  transition: isResizingRight ? 'none' : 'width 0.2s',
                  flexShrink: 0
                }}
              />
              <div
                ref={rightColumnRef}
                className={classes.scrollableGrid}
                style={{
                  flex: 1,
                  minWidth: '200px'
                }}
              >
                <div className="virtual-counter-partner__pay-request-form">
                  {(orderItem.elements.length > 0 || orderItem.orderItemCategory === 'arrangement_request') && (
                    <OrderItemPriceForm
                      index={0}
                      serviceId={serviceId}
                      selectedTrip={trip}
                      orderItem={orderItem}
                      bulkTickets={store.bulkTickets}
                      shareholderTickets={store.shareholderTickets}
                      taxTypes={store.taxTypes}
                      paymentMethods={store.paymentMethods}
                      shareholderInfos={store.shareholderInfos}
                      individualTargetSuppliedItems={store.individualTargetSuppliedItems}
                      classNameForModifiedField={classNameForModifiedField}
                    />
                  )}
                </div>
                <OrderItemMessageLogs traceId={store.traceId} />
                {store.orderItemStatusLogInfo && (
                  <div style={{ padding: '8px' }}>
                    <StatusChangeLog orderItemStatusLogInfo={store.orderItemStatusLogInfo} defaultOpen />
                  </div>
                )}
              </div>
            </>
          </VirtualCounterProvider>
        ) : null}
      </div>
    );
  }
);

const useStyles = makeStyles(() => ({
  scrollableGrid: {
    height: '85vh',
    overflowY: 'auto'
  }
}));

export default OrderItemStepWorkDialogContent;
